import { useAuth } from '@teko/auth-react';
import {
  Body,
  Button,
  Col,
  CustomLink,
  Dropdown,
  LazyImage,
  ListContent,
  Shape,
  Skeleton,
  styled,
  useModal,
} from '@teko/ds-desktop';
import { AccountOutlined } from '@teko/ds-icon';
import { getTrackingData } from '@teko/tracking';
import { Profile } from '@teko/user';
import { useServices, useTenantConfig } from 'contexts';
import { onSignOut } from 'helpers';
import { t } from 'i18n';
import React, { ReactElement } from 'react';
import { NavigationButton } from './style';

export interface UserProfileHeaderProps {
  vertical?: boolean;
  customProfile?: React.ReactNode;
  profileLinks?: ProfileLinkProps[];
}
const Avatar = styled.div`
  width: 36px;
  height: 36px;
  margin: 5px 0 4px 0;
  flex-shrink: 0;
  &.vertical-avatar {
    width: 30px;
    height: 30px;
    margin: 3px;
  }
`;
const AvatarImage = styled(LazyImage)`
  border-radius: 50%;
`;

const BriefUserPanel = styled.div`
  padding: 0.5rem;
  display: flex;
`;
const UserAvatar = styled.div`
  width: 44px;
  margin-right: 0.5rem;
  img {
    width: 100%;
  }
`;
const UserInfo = styled.div`
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  .name {
    font-size: 1rem;
    margin-bottom: 0;
  }
  .extra {
    font-size: 0.9rem;
    font-weight: 400;
  }
`;
const StyledButton = styled(Button)`
  height: 2rem;
  width: 100%;
`;

const DropdownContent = styled.div`
  width: 250px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.15);
`;

const UserName = styled(Body)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

interface ProfileLinkProps {
  icon: ReactElement;
  href: string;
  regionName: string;
  contentName: string;
  text: string;
  onClick?: () => void;
}
const ProfileLink: React.FC<ProfileLinkProps> = ({
  icon,
  text,
  href,
  regionName,
  contentName,
  onClick,
}: ProfileLinkProps) => {
  return (
    <CustomLink href={href}>
      <ListContent
        icon={icon}
        label={text}
        onClick={onClick}
        {...getTrackingData({
          isTrackContent: true,
          regionName,
          contentName,
        })}
      />
    </CustomLink>
  );
};

const RoundSkeleton = styled(Skeleton)`
  border-radius: 50%;
`;
type ParentType = typeof NavigationButton;
interface NavigationButtonWrapperProps extends Partial<ParentType> {
  vertical: boolean;
}

const NavigationButtonWrapper = styled(NavigationButton)(
  (props: NavigationButtonWrapperProps) => `
  width: ${props.vertical ? '90' : '130'}px;
  height:${props.vertical ? 'auto' : '45px'};
  ${props.vertical ? 'flex-direction: column;' : ''}
`,
);

const UserProfilePlaceholder = ({
  vertical = false,
}: {
  vertical: boolean;
}) => {
  return (
    <NavigationButtonWrapper vertical={vertical} className="button">
      <RoundSkeleton width="36px" height="36px" />
      {vertical ? (
        <Skeleton height="0.75rem" width="62px" className="title" />
      ) : (
        <div>
          <Skeleton height="0.75rem" width="60px" marginBottom="s8" />
          <Skeleton height="0.75rem" width="90px" />
        </div>
      )}
    </NavigationButtonWrapper>
  );
};

const UserProfileMenu = ({
  profileLinks,
  userProfile,
}: {
  profileLinks?: ProfileLinkProps[];
  userProfile: Profile;
}) => {
  const { showModal } = useModal();
  const { authService } = useAuth();
  const { notificationService } = useServices();
  const { notificationAppId, epicOmni1825 } = useTenantConfig();
  return (
    <DropdownContent>
      <BriefUserPanel>
        <UserAvatar>
          {userProfile.avatar ? (
            <img src={userProfile.avatar} alt="Avatar" />
          ) : (
            <AccountOutlined size={36} color="textSecondary" />
          )}
        </UserAvatar>
        <UserInfo>
          <h3 className="name">{userProfile.name}</h3>
          <h5 className="extra">
            {userProfile.email || userProfile.telephone}
          </h5>
        </UserInfo>
      </BriefUserPanel>
      {profileLinks &&
        profileLinks.map((profileLink: ProfileLinkProps) => (
          <ProfileLink
            key={profileLink.href}
            icon={profileLink.icon}
            href={profileLink.href}
            text={profileLink.text}
            contentName={profileLink.contentName}
            regionName={profileLink.regionName}
          />
        ))}
      <Shape
        padding="s8"
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'logOut',
        })}
      >
        <StyledButton
          onClick={() => {
            showModal?.({
              title: t('Đăng xuất'),
              children: (
                <div>{t('Bạn có chắc chắn muốn đăng xuất không?')}</div>
              ),
              onOk: () =>
                onSignOut(
                  authService,
                  notificationService,
                  notificationAppId,
                  window.location.origin,
                  epicOmni1825,
                ),
              cancelText: t('Không'),
              okText: t('Có'),
            });
          }}
          variant="primary"
        >
          {t('Đăng xuất')}
        </StyledButton>
      </Shape>
    </DropdownContent>
  );
};

export const UserProfileHeader: React.FunctionComponent<
  UserProfileHeaderProps
> = ({
  vertical = false,
  customProfile,
  profileLinks,
}: UserProfileHeaderProps) => {
  const { user: userProfile, loading: profileLoading, authService } = useAuth();
  const onLogin = () => authService.login();
  if (customProfile) return <>{customProfile}</>;
  if (profileLoading) {
    return <UserProfilePlaceholder vertical={vertical} />;
  }
  if (!userProfile?.userId) {
    return (
      <NavigationButtonWrapper
        vertical={vertical}
        onClick={onLogin}
        className="button"
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'loginIcon',
        })}
      >
        <AccountOutlined size={36} color="textSecondary" />
        {vertical ? (
          <Body
            level={2}
            weight="medium"
            color="textSecondary"
            className="title"
          >
            {t('Đăng nhập')}
          </Body>
        ) : (
          <div className="ml-6 text-left">
            <Body level={2} weight="medium" color="textSecondary">
              {t('Đăng nhập')}
            </Body>
            <Body level={2} weight="medium" color="textSecondary">
              {t('Đăng ký')}
            </Body>
          </div>
        )}
      </NavigationButtonWrapper>
    );
  }
  return (
    <Dropdown
      placement="bottom"
      hidden={false}
      menu={
        <UserProfileMenu
          profileLinks={profileLinks}
          userProfile={userProfile}
        />
      }
      dropdownStyle={{ zIndex: 1 }}
    >
      <NavigationButtonWrapper
        vertical={vertical}
        className="button"
        {...getTrackingData({
          isTrackContent: true,
          regionName: 'headerBar',
          contentName: 'profileIcon',
        })}
      >
        <Col span={4} padding="s0">
          {userProfile.avatar ? (
            <Avatar className={vertical ? 'vertical-avatar' : ''}>
              <AvatarImage
                src={userProfile.avatar}
                height="100%"
                aria-label="Current User Avatar"
              />
            </Avatar>
          ) : (
            <AccountOutlined size={36} color="textSecondary" />
          )}
        </Col>
        {vertical ? (
          <UserName
            level={2}
            weight="medium"
            color="textSecondary"
            className="title"
          >
            {userProfile.name || userProfile.telephone}
          </UserName>
        ) : (
          <Col span={8} padding="s0" className="ml-12 text-left">
            <Body level={2} weight="medium" color="textSecondary">
              {t('Xin chào,')}
            </Body>
            <UserName level={2} weight="medium" color="textSecondary">
              {userProfile.name || userProfile.telephone}
            </UserName>
          </Col>
        )}
      </NavigationButtonWrapper>
    </Dropdown>
  );
};
